import type { IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";

// A record's stored `icon` field carries a user-chosen icon in one of two
// distinguishable string forms, so the single column can hold either kind:
//   - an uploaded image, stored as a `data:` URI (rendered as a bitmap), or
//   - a curated built-in glyph, stored as `mdi:<key>` where <key> is a
//     camelCase key into IconCollection (rendered as a themed vector).
// Any other value (empty, or a legacy/unknown string) resolves to "none", so
// the caller falls back to the record-type default glyph.
export const CURATED_ICON_PREFIX = "mdi:";

export type ParsedIconValue =
    | { kind: "image"; value: string }
    | { kind: "curated"; key: string }
    | { kind: "none" };

export const parseIconValue = (value: unknown): ParsedIconValue => {
    if (typeof value !== "string" || value === "") {
        return { kind: "none" };
    }
    if (value.startsWith("data:")) {
        return { kind: "image", value };
    }
    if (value.startsWith(CURATED_ICON_PREFIX)) {
        return { kind: "curated", key: value.slice(CURATED_ICON_PREFIX.length) };
    }
    return { kind: "none" };
};

// Resolve a stored `icon` value to a curated IconCollection component, or
// undefined when the value is not a (valid) `mdi:<key>` curated reference. An
// `mdi:<key>` whose key is not in IconCollection resolves to undefined so the
// caller degrades gracefully to the record-type default rather than rendering
// nothing.
export const resolveCuratedIcon = (value: unknown): IconLike | undefined => {
    const parsed = parseIconValue(value);
    if (parsed.kind !== "curated") {
        return undefined;
    }
    return IconCollection[parsed.key];
};

// Build the `mdi:<key>` stored form from a curated icon key.
export const toCuratedIconValue = (key: string): string => `${CURATED_ICON_PREFIX}${key}`;
